/**
 * Aggregate candles from a base timeframe into a higher timeframe.
 *
 * The aggregation works by grouping candles into time buckets.
 * For example, M5 candles aggregated to H1 will group by the start of each hour.
 *
 * IMPORTANT: only aggregates from visible candles — never reveals the future.
 */

const TF_MINUTES = {
  M1: 1, M2: 2, M3: 3, M5: 5, M10: 10, M15: 15,
  M30: 30, H1: 60, H2: 120, H4: 240, D1: 1440,
  W1: 10080,
};

const TF_HIERARCHY = ['M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1', 'W1'];

/**
 * Get available higher timeframes for a given base timeframe.
 */
export function getAvailableTimeframes(baseTf) {
  const baseIdx = TF_HIERARCHY.indexOf(baseTf);
  if (baseIdx === -1) {
    // Unknown TF, try to match by minutes
    const baseMin = TF_MINUTES[baseTf];
    if (!baseMin) return [];
    return TF_HIERARCHY.filter((tf) => TF_MINUTES[tf] > baseMin);
  }
  return TF_HIERARCHY.slice(baseIdx + 1);
}

/**
 * Get the bucket start timestamp for a given time and target timeframe.
 */
function getBucketStart(timestampSec, targetTf) {
  const date = new Date(timestampSec * 1000);

  if (targetTf === 'W1') {
    // Week starts on Monday
    const d = new Date(date);
    d.setUTCHours(0, 0, 0, 0);
    const day = d.getUTCDay();
    const diff = day === 0 ? 6 : day - 1; // Monday = 0
    d.setUTCDate(d.getUTCDate() - diff);
    return Math.floor(d.getTime() / 1000);
  }

  if (targetTf === 'D1') {
    const d = new Date(date);
    d.setUTCHours(0, 0, 0, 0);
    return Math.floor(d.getTime() / 1000);
  }

  // For intraday TFs, align to the TF period within the day
  const minutes = TF_MINUTES[targetTf];
  if (!minutes) return timestampSec;

  const d = new Date(date);
  const dayStart = new Date(d);
  dayStart.setUTCHours(0, 0, 0, 0);
  const dayStartSec = Math.floor(dayStart.getTime() / 1000);
  const elapsedMinutes = Math.floor((timestampSec - dayStartSec) / 60);
  const bucketMinutes = Math.floor(elapsedMinutes / minutes) * minutes;

  return dayStartSec + bucketMinutes * 60;
}

/**
 * Aggregate an array of candles (Lightweight Charts format) to a higher timeframe.
 *
 * @param {Array} candles - [{time, open, high, low, close, volume}, ...]
 * @param {string} targetTf - e.g. 'H1', 'H4', 'D1'
 * @returns {Array} aggregated candles in the same format
 */
export function aggregateCandles(candles, targetTf) {
  if (!candles || candles.length === 0) return [];

  const buckets = new Map();

  for (const candle of candles) {
    const bucketTime = getBucketStart(candle.time, targetTf);

    if (buckets.has(bucketTime)) {
      const bucket = buckets.get(bucketTime);
      bucket.high = Math.max(bucket.high, candle.high);
      bucket.low = Math.min(bucket.low, candle.low);
      bucket.close = candle.close;
      bucket.volume += candle.volume;
    } else {
      buckets.set(bucketTime, {
        time: bucketTime,
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
        volume: candle.volume,
      });
    }
  }

  return Array.from(buckets.values()).sort((a, b) => a.time - b.time);
}
